import { DatePicker } from '@components/common';
import { usePropertyPayments } from '@hooks/accommodation';
import { useAppSelector } from '@hooks/useRedux';
import { addDaysISO, formatShortDate } from '@lib/utils';
import { selectAccommodationProperty } from '@store/accommodation';
import { Download } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { exportReportCsv } from '../../../../services/reportsService';
import { apiError, apiSuccess } from '../../../../util/notify';
import { fmtAmount, fmtDate } from '../reports/reportUtils';

const PAGE_SIZE = 20;

const today = new Date().toISOString().slice(0, 10);
const defaultFrom = addDaysISO(today, -30);

export const AccommodationPaymentsPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const currency = property?.currency ?? '';

  const [dateFrom, setDateFrom] = useState<string>(defaultFrom);
  const [dateTo, setDateTo] = useState<string>(today);
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const { data: payments = [], isLoading } = usePropertyPayments(property?.id, {
    dateFrom,
    dateTo,
  });

  const totalPages = Math.max(1, Math.ceil(payments.length / PAGE_SIZE));
  const paginated = payments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const pageNums = useMemo(() => {
    type PageEntry = { key: string; val: number | '…' };
    const e = (v: number | '…', k: string): PageEntry => ({ key: k, val: v });
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) =>
        e(i + 1, String(i + 1)),
      );
    if (page <= 4)
      return [
        e(1, '1'),
        e(2, '2'),
        e(3, '3'),
        e(4, '4'),
        e(5, '5'),
        e('…', 'el1'),
        e(totalPages, 'last'),
      ];
    if (page >= totalPages - 3)
      return [
        e(1, '1'),
        e('…', 'el1'),
        e(totalPages - 4, String(totalPages - 4)),
        e(totalPages - 3, String(totalPages - 3)),
        e(totalPages - 2, String(totalPages - 2)),
        e(totalPages - 1, String(totalPages - 1)),
        e(totalPages, 'last'),
      ];
    return [
      e(1, '1'),
      e('…', 'el1'),
      e(page - 1, String(page - 1)),
      e(page, String(page)),
      e(page + 1, String(page + 1)),
      e('…', 'el2'),
      e(totalPages, 'last'),
    ];
  }, [page, totalPages]);

  const handleExport = async () => {
    if (payments.length === 0) return;
    setIsExporting(true);
    try {
      const columns = [
        { key: 'paid_at', header: 'Payment Date' },
        { key: 'booking_ref', header: 'Booking Ref' },
        { key: 'guest', header: 'Guest' },
        { key: 'amount', header: `Amount (${currency})` },
        { key: 'payment_method', header: 'Payment Method' },
        { key: 'notes', header: 'Notes' },
      ];
      const rows = payments.map((p) => ({
        paid_at: fmtDate(p.paid_at),
        booking_ref: `#${p.booking_id.slice(0, 8).toUpperCase()}`,
        guest:
          [p.client_first_name, p.client_last_name].filter(Boolean).join(' ') ||
          '—',
        amount: fmtAmount(p.amount),
        payment_method: p.payment_method_name ?? '—',
        notes: p.notes ?? '',
      }));
      await exportReportCsv({
        title: 'Accommodation Payments',
        fileName: `accommodation-payments-${new Date().toISOString().slice(0, 10)}.csv`,
        columns,
        rows,
      });
      apiSuccess('Exported successfully.');
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* ── Toolbar ──────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <DatePicker
          mode="range"
          startDate={dateFrom}
          endDate={dateTo}
          onRangeChange={(s, e) => {
            setDateFrom(s);
            setDateTo(e ?? s);
            setPage(1);
          }}
          placeholder="Select period"
          label=""
          id="payments-period"
        />
        <button
          type="button"
          onClick={handleExport}
          disabled={isExporting || payments.length === 0}
          className="flex h-8 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Download size={12} />
          Export CSV
        </button>
      </div>

      {/* ── Table ────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-200 bg-white">
        {isLoading && payments.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-gray-400">
            Loading…
          </div>
        ) : payments.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-gray-400">
            No payments found for the selected period
          </div>
        ) : (
          <>
            <div className="max-h-[calc(100vh-260px)] overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">
                      Payment Date
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">
                      Booking Ref
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">
                      Guest
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">
                      Amount ({currency})
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">
                      Payment Method
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">
                      Notes
                    </th>
                    <th className="px-4 py-3 text-center font-medium text-gray-500">
                      Booking
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginated.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-gray-700">
                        {formatShortDate(p.paid_at)}
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-500">
                        #{p.booking_id.slice(0, 8).toUpperCase()}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {[p.client_first_name, p.client_last_name]
                          .filter(Boolean)
                          .join(' ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {fmtAmount(p.amount)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {p.payment_method_name ?? '—'}
                      </td>
                      <td className="max-w-[160px] truncate px-4 py-3 text-gray-500">
                        {p.notes ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Link
                          to={`/accommodation/bookings/${p.booking_id}`}
                          className="text-pos-primary hover:underline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ─────────────────────────────────── */}
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
              <p className="text-xs text-gray-400">
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, payments.length)}–
                {Math.min(page * PAGE_SIZE, payments.length)} of{' '}
                {payments.length} payments
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="flex h-7 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  ← Previous
                </button>
                {pageNums.map(({ key, val }) =>
                  val === '…' ? (
                    <span key={key} className="px-1 text-xs text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setPage(val as number)}
                      className={[
                        'flex h-7 w-7 items-center justify-center rounded-lg border text-xs',
                        page === val
                          ? 'border-pos-primary bg-pos-primary font-semibold text-white'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {val}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="flex h-7 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
